/**
 * SidebarLicense — upgrade prompt shown in the sidebar for free/expiring users.
 *
 * Visibility rules (v1-exact):
 * - Hidden if user has an active paid Standard plan
 * - Hidden if user is on Demo plan
 * - Hidden if trial expires in 7+ days
 * - Shown for Free users (no sync/backup)
 * - Shown for trial users expiring within 7 days
 *
 * Notion-style: compact, inline in sidebar, not a modal.
 */

import { memo } from "react"
import { useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import AlertTriangle from "@/shared/icons/pikaicons/stroke/alerts/alert-triangle.svg?react"
import ArrowUpCircle from "@/shared/icons/pikaicons/stroke/arrows-chevrons/arrow-up-circle.svg?react"
import Rocket from "@/shared/icons/pikaicons/stroke/general/rocket-ship.svg?react"
import dayjs from "dayjs"
import { Button } from "@/app/components/ui/button"
import { useCurrentSubscription } from "@/features/settings/hooks/useCurrentSubscription"
import {
  PaymentPlan,
  PaymentTerm,
} from "@/features/settings/data/subscriptionPlans"

export const SidebarLicense = memo(function SidebarLicense() {
  const { t } = useTranslation()
  const [, setSearchParams] = useSearchParams()
  const info = useCurrentSubscription()

  if (!info) return null

  const { currentSubscription: sub } = info

  // Active paid Standard → hide
  if (sub.plan === PaymentPlan.Standard && sub.term !== PaymentTerm.Free) {
    return null
  }

  // Demo plan → hide
  if (sub.plan === PaymentPlan.Demo) return null

  // Trial with 7+ days remaining → hide
  if (
    sub.plan === PaymentPlan.Standard &&
    sub.term === PaymentTerm.Free &&
    dayjs(Number(sub.expires || 0)).diff(dayjs(), "days") >= 7
  ) {
    return null
  }

  const isFree = sub.plan === PaymentPlan.Free
  const expiresInDays = dayjs(Number(sub.expires || 0)).diff(dayjs(), "day")

  const handleUpgrade = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set("setting", "account")
        return next
      },
      { replace: true },
    )
  }

  return (
    <div className="mx-4 p-4">
      {/* Icon */}
      {isFree ? (
        <AlertTriangle className="mb-1 size-5 text-primary" />
      ) : (
        <ArrowUpCircle className="mb-1 size-5 text-primary" />
      )}

      {/* Title */}
      <p className="text-sm font-semibold text-foreground">
        {isFree ? t("Offline") : t("Stay in sync")}
      </p>

      {/* Description */}
      <p className="mt-0.5 text-xs text-muted-foreground">
        {isFree
          ? t("Sync, backup, and AI features are currently unavailable.")
          : t(
              "Your trial expires in {{days}} days. Upgrade to keep syncing and AI features.",
              { days: expiresInDays },
            )}
      </p>

      {/* CTA */}
      <Button size="small" className="mt-2.5 w-full" onClick={handleUpgrade}>
        <Rocket className="mr-1.5 size-4" />
        {t("Upgrade")}
      </Button>
    </div>
  )
})
